/**
 * ResourcesView — family knowledge base.
 * Per app-codex: surface "resources.list". Behaviour: resources.create, resources.search.
 * Dev mode: uses devDb.
 */
import { useEffect, useState } from 'react'
import { fetchResources, createResource, type DbResource } from '../db'
import { devDb } from '../devMode'
import { useApp } from '../store'
import { Plus, BookOpen, Wrench, Link as LinkIcon, FileText } from 'lucide-react'

const ICONS = {
  article: BookOpen,
  tool: Wrench,
  link: LinkIcon,
  file: FileText,
} as const

export function ResourcesView() {
  const { currentMember, dev } = useApp()
  const [resources, setResources] = useState<DbResource[]>([])
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [type, setType] = useState<DbResource['type']>('article')

  useEffect(() => {
    load()
  }, [])

  async function load() {
    if (dev) {
      const { data } = await devDb.resources.list()
      setResources(data ?? [])
    } else {
      const { data } = await fetchResources()
      setResources(data ?? [])
    }
  }

  async function add(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim() || !currentMember) return
    if (dev) {
      // dev mode resources are read-only in this view
    } else {
      await createResource({
        title,
        type,
        subjects: [],
        tags: [],
        created_by: currentMember.id,
      })
    }
    setTitle('')
    setShowForm(false)
    load()
  }

  const filtered = resources.filter((r) =>
    search ? r.title.toLowerCase().includes(search.toLowerCase()) : true
  )

  return (
    <div>
      <header className="mb-6 flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-skin-text">Resources</h1>
          <p className="text-slate-500 mt-1">Things we've found helpful.</p>
        </div>
      </header>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search resources…"
        className="w-full mb-4 px-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-garden-500 min-h-touch"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((r) => {
          const Icon = ICONS[r.type]
          return (
            <article key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-garden-100 text-garden-800 flex items-center justify-center">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-skin-text truncate">{r.title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{r.description}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-3">
                {r.tags.map((t) => (
                  <span key={t} className="text-xs text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">#{t}</span>
                ))}
              </div>
            </article>
          )
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-12">No resources yet.</div>
        )}
      </div>
    </div>
  )
}
